<script lang="ts">
	import Video from '$lib/video.svelte';
	import type { SbMuxVideo } from '../types';
	import Expand from '$lib/icons/expand.svelte';
	import FullscreenVideo from '$lib/fullscreenVideo.svelte';
	import MuteButton from '../../buttons/v3/muteButton.svelte';
	import BtnProgressPause from '$lib/buttons/v3/btnProgressPause.svelte';
	import BtnPause from '$lib/buttons/v3/btnPause.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';

	interface Props {
		content: ISbStoryData<SbMuxVideo>['content'];
		hideControls?: boolean;
		threshold?: number;
	}

	let { content, hideControls = false, threshold = 0 }: Props = $props();

	let videoElement = $state<HTMLVideoElement>();
	let videoLoaded = $state(false);
	let expandedVideo = $state(false);

	$effect(() => {
		if (!videoElement || !videoLoaded) return;

		if (expandedVideo && !videoElement.paused) {
			videoElement.pause();
		} else if (content.autoplay && videoElement.paused) {
			videoElement.play();
		}
	});

	function videoClick() {
		if (!videoElement) return;

		switch (content.video_click_action) {
			case 'fullscreen':
				expandedVideo = true;
				break;
			case 'mute':
				videoElement.muted = !videoElement.muted;
				break;
			case 'pause':
				videoElement.paused ? videoElement.play() : videoElement.pause();
				break;
		}
	}
</script>

<div use:editable={content} class="group relative h-full w-full @container">
	<Video
		autoplay={content.autoplay ? (content.mute ? 'muted' : 'any') : null}
		loop={content.loop}
		muted={content.mute}
		fit={content.fit}
		wrapperClass="h-full w-full rounded-{content.rounded_corners ?? 'none'}"
		class={['h-full w-full', `object-${content.object_fit ?? 'cover'}`]}
		playbackId={content.playback_id}
		onloaded={() => (videoLoaded = true)}
		{threshold}
		bind:videoElement
	/>
	{#if !hideControls && videoElement}
		{#if content.control_mute || content.control_play || content.control_fullscreen || content.control_progress_circle}
			<div
				class={[
					'absolute z-20 flex w-full gap-x-2',
					{
						'bottom-4': content.controls_position !== 'top-right',
						'top-4': content.controls_position === 'top-right',
						'justify-center':
							!content.controls_position || content.controls_position === 'bottom-center',
						'justify-end': ['bottom-right', 'top-right'].includes(content.controls_position),
						'right-4': ['bottom-right', 'top-right'].includes(content.controls_position),
						'left-4': content.controls_position === 'bottom-left'
					}
				]}
			>
				{#if content.control_mute}
					<MuteButton {videoElement} />
				{/if}
				{#if content.control_play}
					<BtnPause {videoElement} />
				{/if}
				{#if content.control_fullscreen}
					<Button class="btn-blur" onclick={() => (expandedVideo = true)}>
						<Expand class="h-5 w-5 text-stone-5" />
					</Button>
				{/if}
				{#if content.control_progress_circle}
					<BtnProgressPause {videoElement} />
				{/if}
			</div>
		{/if}
		{#if content.video_click_action}
			<button
				type="button"
				onclick={videoClick}
				class="absolute inset-0 z-10"
				aria-label={content.video_click_action}
			>
			</button>
		{/if}
		{#if content.control_fullscreen_top_right}
			<div
				class="absolute right-2 top-2 z-20 @md:right-4 @md:top-4 lg:right-4 lg:top-4 @md:lg:right-6 @md:lg:top-6"
			>
				<button
					type="button"
					title="Expand video"
					onclick={() => (expandedVideo = true)}
					class="rounded-full bg-black/20 p-3 backdrop-blur"
				>
					<Expand class="h-5 w-5 text-stone-5 lg:h-6 lg:w-6" />
				</button>
			</div>
			<button
				type="button"
				title="Expand video"
				onclick={() => (expandedVideo = true)}
				class="absolute inset-0 z-10"
				aria-label="Expand video"
			>
			</button>
		{/if}
		{#if content.control_play_bottom_right}
			<div class="absolute bottom-0 right-0 m-3 lg:m-4">
				<BtnPause {videoElement} />
			</div>
		{/if}
	{/if}
</div>

{#if expandedVideo}
	<FullscreenVideo
		backgroundWithOpacity
		buttonPos="top-right"
		playbackId={content.playback_id}
		onclose={() => (expandedVideo = false)}
	/>
{/if}
